const ortText = (key, count) => {
  const text = JSON.parse(document.getElementById('OrtTranslations')?.textContent || '{}')[key] || '';
  return count === undefined ? text : text.replace('__COUNT__', String(count));
};

class OrtPdpRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.submitBound) {
      this.addEventListener('submit', this.addProduct);
      this.submitBound = true;
    }
    if (this.loaded || this.controller) return;
    this.controller = new AbortController();
    this.load(this.controller.signal);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
  }

  async addProduct(event) {
    const form = event.target.closest('[data-ort-recommendation-form]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    if (!button || button.disabled) return;
    const status = form.closest('[data-recommendation-product-id]').querySelector('[data-ort-recommendation-status]');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    status.textContent = ortText('adding');
    try {
      const url = new URL(form.action);
      url.pathname += '.js';
      const response = await fetch(url, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || ortText('add_error'));
      status.textContent = ortText('added');
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
      try {
        const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          document.querySelectorAll('.ort-cart-count').forEach(counter => {
            counter.textContent = String(cart.item_count || 0);
            counter.closest('a')?.setAttribute('aria-label', ortText('cart_count', cart.item_count || 0));
          });
        }
      } catch {}
    } catch (error) {
      status.textContent = error instanceof Error ? error.message : ortText('error');
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  async getProducts(intent, signal) {
    const url = new URL(this.dataset.url, window.location.origin);
    url.searchParams.set('intent', intent);
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`Recommendations returned ${response.status}`);
    const document = new DOMParser().parseFromString(await response.text(), 'text/html');
    return [...document.querySelectorAll('[data-ort-recommendation-result] [data-recommendation-product-id]')]
      .filter((item) => item.dataset.recommendationProductId !== this.dataset.productId);
  }

  async load(signal) {
    try {
      let intent = 'complementary';
      let products = await this.getProducts(intent, signal);
      if (!products.length) {
        intent = 'related';
        products = await this.getProducts(intent, signal);
      }
      if (signal.aborted || !this.isConnected) return;
      this.loaded = true;
      this.dataset.intent = intent;
      if (!products.length) return;
      this.querySelector('[data-ort-recommendation-items]').replaceChildren(...products);
      this.querySelector('[data-ort-recommendation-heading]').textContent =
        intent === 'complementary' ? this.dataset.complementaryHeading : this.dataset.relatedHeading;
      this.hidden = false;
    } catch (error) {
      if (error.name !== 'AbortError') this.dataset.loadError = 'true';
    }
  }
}

if (!customElements.get('ort-pdp-recommendations')) {
  customElements.define('ort-pdp-recommendations', OrtPdpRecommendations);
}

(() => {
  const moneyText = (value) => value || '';

  const initializeProduct = (root) => {
    if (root.dataset.ortInitialized) return;
    root.dataset.ortInitialized = 'true';
    const form = root.querySelector('[data-ort-product-form]');
    const quantity = root.querySelector('[data-ort-quantity]');
    const status = root.querySelector('[data-ort-product-status]');
    const addButton = root.querySelector('[data-ort-add]');
    const stickyBuy = root.querySelector('[data-ort-sticky-buy]');
    const stickyAdd = root.querySelector('[data-ort-sticky-add]');
    const stickyStatus = root.querySelector('[data-ort-sticky-status]');
    const stickyPrice = root.querySelector('[data-ort-sticky-price]');
    const stickyVariant = root.querySelector('[data-ort-sticky-variant]');
    const price = root.querySelector('[data-ort-product-price] strong');
    const variantSelect = root.querySelector('[data-ort-variant-select]');
    let adding = false;
    let available = !addButton?.disabled;
    const setStatus = (message) => {
      if (status) status.textContent = message;
      if (stickyStatus) stickyStatus.textContent = message;
    };
    const syncButtons = () => {
      [addButton, stickyAdd].forEach((button) => {
        if (!button) return;
        button.disabled = adding || !available;
        button.setAttribute('aria-busy', String(adding));
        const label = button.querySelector('span') || button;
        label.textContent = adding ? ortText('adding') : ortText(available ? 'add_to_cart' : 'sold_out');
      });
    };
    if (stickyBuy && addButton && form) {
      let scrollFrame;
      const syncVisibility = () => {
        scrollFrame = undefined;
        stickyBuy.hidden = addButton.getBoundingClientRect().bottom > 0;
      };
      const onScroll = () => {
        if (scrollFrame === undefined) scrollFrame = requestAnimationFrame(syncVisibility);
      };
      const observer = new IntersectionObserver(([entry]) => {
        stickyBuy.hidden = entry.isIntersecting || entry.boundingClientRect.bottom > 0;
      });
      observer.observe(addButton);
      // Large scroll jumps can skip both intersection boundaries.
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      const section = root.closest('.shopify-section');
      section?.addEventListener('shopify:section:unload', () => {
        observer.disconnect();
        cancelAnimationFrame(scrollFrame);
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }, { once: true });
    }

    const updateQuantity = (delta) => {
      if (!quantity) return;
      const next = Math.min(10, Math.max(1, Number(quantity.value || 1) + delta));
      quantity.value = String(next);
    };
    root.querySelector('[data-ort-quantity-minus]')?.addEventListener('click', () => updateQuantity(-1));
    root.querySelector('[data-ort-quantity-plus]')?.addEventListener('click', () => updateQuantity(1));

    variantSelect?.addEventListener('change', () => {
      const option = variantSelect.selectedOptions[0];
      available = option?.dataset.available === 'true';
      root.querySelectorAll('.ort-pdp__stock').forEach((stock) => {
        stock.classList.toggle('is-out', !available);
        const label = stock.querySelector('[data-ort-stock-label]');
        if (label) label.textContent = available ? ortText('in_stock') : ortText('sold_out');
      });
      root.querySelectorAll('.ort-pdp__meta .ort-pdp__badge').forEach((badge) => {
        badge.hidden = !available;
      });
      if (price) price.textContent = moneyText(option?.dataset.price);
      if (stickyPrice) stickyPrice.textContent = moneyText(option?.dataset.price);
      if (stickyVariant) stickyVariant.textContent = option?.dataset.title || '';
      setStatus('');
      syncButtons();
    });

    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!addButton || addButton.disabled) return;
      adding = true;
      syncButtons();
      setStatus(ortText('adding'));
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        if (!response.ok) throw new Error(ortText('add_error'));
        const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
        });
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          document.querySelectorAll('.ort-cart-count').forEach((counter) => {
            counter.textContent = String(cart.item_count || 0);
          });
        }
        setStatus(ortText('product_added'));
        document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
      } catch (error) {
        setStatus(error instanceof Error ? error.message : ortText('error'));
      } finally {
        adding = false;
        syncButtons();
      }
    });

    stickyAdd?.addEventListener('click', () => form?.requestSubmit());

    const gallery = root.querySelector('[data-ort-gallery]');
    if (!gallery) return;
    const dialog = gallery.querySelector('[data-ort-gallery-dialog]');
    const dialogImage = gallery.querySelector('[data-ort-gallery-dialog-image]');
    gallery.querySelectorAll('[data-ort-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const mediaId = thumb.dataset.ortGalleryThumb;
        gallery.querySelectorAll('[data-media-id]').forEach((slide) => {
          const active = slide.dataset.mediaId === mediaId;
          slide.hidden = !active;
          slide.classList.toggle('is-active', active);
          if (active && dialogImage) {
            const image = slide.querySelector('img');
            dialogImage.src = image?.currentSrc || image?.src || '';
            dialogImage.alt = image?.alt || '';
          }
        });
        gallery.querySelectorAll('[data-ort-gallery-thumb]').forEach((item) => {
          const active = item === thumb;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
      });
    });
    gallery.querySelector('[data-ort-gallery-open]')?.addEventListener('click', () => dialog?.showModal());
    gallery.querySelector('[data-ort-gallery-close]')?.addEventListener('click', () => dialog?.close());
    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
  };
  document.querySelectorAll('[data-ort-product]').forEach(initializeProduct);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-ort-product]').forEach(initializeProduct);
  });
})();
